import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Editor } from "@tinymce/tinymce-react";
import secureLocalStorage from "react-secure-storage";

const ExperienceInfo = () => {
  const [form, setForm] = useState({});
  const [experienceList, setExperienceList] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(true);

	const [error, setError] = useState(null);
	const experiencePost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				user: secureLocalStorage.getItem("user"),
				token:secureLocalStorage.getItem("token"),
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/experience-list", payload);
			
			// Loop data (array)
			setExperienceList(response.data || []);			
		} catch (err) {
			console.log(err);
		    setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {		
		experiencePost();		
	}, []);

  // ✅ Input and editor change handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditorChange = (content) => {
    setForm((prev) => ({ ...prev, responsibilities: content }));
  };

	const handleSave = async () => {
	  try {
		// Create a FormData payload
		const payload = new FormData();

		// Add secure credentials
		payload.append("userId", import.meta.env.VITE_API_USER);
		payload.append("password", import.meta.env.VITE_API_PASS);
		payload.append("user", secureLocalStorage.getItem("user"));
		payload.append("token", secureLocalStorage.getItem("token"));
		// Append education form fields
		Object.entries(form).forEach(([key, value]) => {
		  if (value !== undefined && value !== null) {
			payload.append(key, value);
		  }
		});

		let res;

		// --- Update existing education record ---
		if (editingIndex !== null) {
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const res = await api.post("/update-experience", payload);		 

			// Update local state list
			const updated = [...experienceList];
			updated[editingIndex] = res.data;
			setExperienceList(res.data);
			setEditingIndex(null);
		}else {
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const res = await api.post("/update-experience", payload);

			setExperienceList(res.data);
		}

		// --- Reset form ---
		setForm({});
		
		// --- Handle response feedback ---
		const code = res?.data?.responseCode?.toString();
		if (code === "200") {
		  alert(res?.data?.responseMsg || "✅ Experience info saved successfully!");
		} else {
		  alert(res?.data?.responseMsg || "✅ Experience info updated successfully!");
		}
	  } catch (err) {
		console.error("Error saving experience:", err);
		alert("❌ Failed to save experience info. Please try again.");
	  }
	};
  

  const handleEdit = (index) => {
    setEditingIndex(index);
    setForm(experienceList[index]);
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setForm({});
  };

  if (loading)
    return <div className="text-center text-blue-600 py-8">Loading Experience...</div>;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-6 shadow-md space-y-8"
    >
      <h2 className="text-2xl font-semibold text-[#1A2B63] mb-4">
        Experience Information
      </h2>

      {/* =================== FORM =================== */}
      <form className="grid grid-cols-2 md:grid-cols-3 gap-5">
        {/* Row 1 */}
        {[
          { label: "Organization Name", name: "organization" },
          { label: "Designation", name: "designation" },
          { label: "Location", name: "location" },
          { label: "Department", name: "department" },
          { label: "Industry Type", name: "industryType" },
        ].map((field, i) => (
          <div key={i} className="flex flex-col">
            <label className="text-gray-700 text-sm font-semibold mb-1">
              {field.label}
            </label>
            <input
              name={field.name}
              value={form[field.name] || ""}
              onChange={handleChange}
              className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
            />
          </div>
        ))}

        {/* Start / End Date */}
        <div className="flex flex-col">
          <label className="text-gray-700 text-sm font-semibold mb-1">
            Start Date
          </label>
          <input
            name="startDate"
            type="date"
            value={form.startDate || ""}
            onChange={handleChange}
            className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
          />
        </div>

        {!form.currentlyWorking && (
          <div className="flex flex-col">
            <label className="text-gray-700 text-sm font-semibold mb-1">
              End Date
            </label>
            <input
              name="endDate"
              type="date"
              value={form.endDate || ""}
              onChange={handleChange}
              className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
            />
          </div>
        )}

        <div className="col-span-full flex items-center gap-2">
          <input
            type="checkbox"
            name="currentlyWorking"
            checked={form.currentlyWorking || false}
            onChange={handleChange}
            id="currentlyWorking"
          />
          <label htmlFor="currentlyWorking" className="text-gray-700">
            I am currently working in this role
          </label>
        </div>

        {/* Supervisor Info */}
        {[
          { label: "Supervisor's Name", name: "supervisorName" },
          { label: "Supervisor's Email", name: "supervisorEmail", type: "email" },
          { label: "Supervisor's Phone", name: "supervisorPhone" },
        ].map((field, i) => (
          <div key={i} className="flex flex-col">
            <label className="text-gray-700 text-sm font-semibold mb-1">
              {field.label}
            </label>
            <input
              type={field.type || "text"}
              name={field.name}
              value={form[field.name] || ""}
              onChange={handleChange}
              className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
            />
          </div>
        ))}

        {/* Achievement */}
        <div className="flex flex-col col-span-full">
          <label className="text-gray-700 text-sm font-semibold mb-1">
            Achievement
          </label>
          <textarea
            name="achievement"
            value={form.achievement || ""}
            onChange={handleChange}
            className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200 min-h-[80px]"
          />
        </div>

        {/* Area of Expertise */}
        <div className="flex flex-col col-span-full">
          <label className="text-gray-700 text-sm font-semibold mb-1">
            Area of Expertise
          </label>
          <textarea
            name="expertise"
            value={form.expertise || ""}
            onChange={handleChange}
            className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200 min-h-[80px]"
          />
        </div>

        {/* Responsibilities as Editor (TinyMCE) */}
        <div className="col-span-full">
          <label className="block text-gray-700 text-sm font-semibold mb-2">
            Responsibilities
          </label>
          <Editor
            apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
            value={form.responsibilities || ""}
            onEditorChange={handleEditorChange}
            init={{
              height: 300,
              menubar: false,
              plugins: [
                "advlist autolink lists link charmap preview anchor",
                "searchreplace visualblocks code fullscreen",
                "insertdatetime media table help wordcount",
              ],
              toolbar:
                "undo redo | formatselect | bold italic underline | bullist numlist | alignleft aligncenter alignright | link | code | removeformat",
              content_style:
                "body { font-family:Inter,Arial,sans-serif; font-size:14px; }",
            }}
          />
        </div>

        {/* Buttons */}
        <div className="col-span-full flex justify-end gap-3 mt-4">
          {editingIndex !== null && (
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="bg-blue-600 text-white"
          >
            {editingIndex !== null ? "Update Experience" : "Add Experience"}
          </Button>
        </div>
      </form>

      {/* =================== EXPERIENCE LIST =================== */}
      <div className="mt-8 space-y-4">
        <h3 className="text-xl font-semibold text-[#1A2B63]">
          Experience History
        </h3>

        {Array.isArray(experienceList) && experienceList.length > 0 ? (
          experienceList.map((exp, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-blue-50 rounded-lg flex justify-between items-start shadow-sm hover:shadow-md transition-all"
            >
              <div className="space-y-1 w-full">
                <p className="font-semibold text-blue-800">
                  {exp.designation} @ {exp.organization}
                </p>
                <p className="text-sm text-gray-700">
                  {exp.department} | {exp.industryType}
                </p>
                <p className="text-sm text-gray-600">
                  {exp.startDate} - {exp.endDate === "0000-00-00" || !exp.endDate ? "Present" : exp.endDate}
                </p>
                {exp.supervisorName && (
                  <p className="text-sm text-gray-600">
                    <strong>Supervisor:</strong> {exp.supervisorName} (
                    {exp.supervisorEmail})
                  </p>
                )}
                {exp.achievement && (
                  <p className="text-sm text-gray-500 italic">
                    Achievement: {exp.achievement}
                  </p>
                )}
                {exp.responsibilities && (
                  <div
                    className="text-sm text-gray-700 mt-2 prose"
                    dangerouslySetInnerHTML={{ __html: exp.responsibilities }}
                  />
                )}
              </div>
              <Button size="sm" variant="outline" onClick={() => handleEdit(i)}>
                Edit
              </Button>
            </motion.div>
          ))
        ) : (
          <p className="text-gray-500 italic text-center">
            No experience records added yet.
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default ExperienceInfo;
